import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Tool } from '../types';

interface FavoritesContextType {
  favorites: Tool[];
  addToFavorites: (tool: Tool) => void;
  removeFromFavorites: (toolId: string) => void;
  isFavorite: (toolId?: string | null) => boolean;
  toggleFavorite: (tool?: Tool | null) => void;
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined);

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (context === undefined) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

interface FavoritesProviderProps {
  children: ReactNode;
}

export const FavoritesProvider: React.FC<FavoritesProviderProps> = ({ children }) => {
  const [favorites, setFavorites] = useState<Tool[]>([]);

  const addToFavorites = (tool: Tool) => {
    setFavorites(prev => {
      if (prev.some(fav => fav.id === tool.id)) {
        return prev; // Already in favorites
      }
      const newFavorites = [...prev, tool];
      localStorage.setItem('domore_favorites', JSON.stringify(newFavorites));
      return newFavorites;
    });
  };

  const removeFromFavorites = (toolId: string) => {
    setFavorites(prev => {
      const newFavorites = prev.filter(tool => tool.id !== toolId);
      localStorage.setItem('domore_favorites', JSON.stringify(newFavorites));
      return newFavorites;
    });
  };

  const isFavorite = (toolId?: string | null) => {
    return favorites.some(tool => tool.id === toolId);
  };

  const toggleFavorite = (tool?: Tool | null) => {
    if (tool) {
      if (isFavorite(tool.id)) {
        removeFromFavorites(tool.id);
      } else {
        addToFavorites(tool);
      }
    }
  };

  // Load favorites from localStorage on mount
  React.useEffect(() => {
    const savedFavorites = localStorage.getItem('domore_favorites');
    if (savedFavorites) {
      try {
        setFavorites(JSON.parse(savedFavorites));
      } catch (error) {
        console.error('Failed to load favorites from localStorage:', error);
      }
    }
  }, []);

  const value = {
    favorites,
    addToFavorites,
    removeFromFavorites,
    isFavorite,
    toggleFavorite,
  };

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
};